import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { shipmentMissingService } from "../../services/shipmentMissingService"
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from "../common/Loader.js"
import Alert from "../common/Alert"
import Button from '../common/Button.js'
import { ArrowLeft, AlertTriangle } from "lucide-react";
import { validators, useValidation, ValidationMessage } from "../common/Validation.js";
import { useTranslation } from 'react-i18next';
import { formLabelClass, formTextareaClass } from '../common/uiClasses.js';
import { formatIstDateTime } from '../../utils/localDate.js';


function InfoField({ label, value, className = '', children }) {
  if (!children && (value === null || value === undefined || value === '')) return null;
  return (
    <div className={className}>
      <p className="text-xs sm:text-sm text-gray-600 font-semibold">{label}</p>
      {children || (
        <p className="font-medium text-gray-700 text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1">
          {value}
        </p>
      )}
    </div>
  );
}

function SectionCard({ title, children, className = 'mb-4 sm:mb-6' }) {
  return (
    <div className={`bg-white rounded-xl shadow-md p-4 sm:p-5 ${className}`}>
      <h2 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
        {title}
      </h2>
      {children}
    </div>
  );
}

const EditMissing = () => {
  const { t } = useTranslation();
  const { missingId } = useParams()
  const navigate = useNavigate()
  const { errors, validate, validateField } = useValidation()

  const validationRules = {
    rejectionReason: [(v) => validators.required(v, 'Rejection reason'), (v) => v && v.trim().length < 10 ? 'Rejection reason must be at least 10 characters' : ''],
  }

  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)
  const [missingData, setMissingData] = useState(null)
  const [rejectionReason, setRejectionReason] = useState("")

  useEffect(() => {
    fetchMissing()
  }, [missingId])

  const fetchMissing = async () => {
    try {
      setLoading(true)
      const response = await shipmentMissingService.getMissing(missingId)
      const missing = response.data.missing || response.data.data

      if (missing) {
        setMissingData(missing)
      } else {
        Alert.error("Missing shipment record not found")
      }
    } catch (error) {
      console.error("Error fetching missing:", error)
      Alert.error(error.response?.data?.error || "Failed to load missing shipment")
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async () => {
    const confirmed = await Alert.confirm(
      "Are you sure you want to approve this missing shipment report?",
      "Approve Missing Report",
    )

    if (!confirmed) return

    try {
      setUpdating(true)

      await shipmentMissingService.approveMissing(missingId)

      Alert.success("Missing shipment report approved successfully!")
      navigate("/missing-shipments")
    } catch (error) {
      console.error("Error approving missing:", error)
      Alert.error(error.response?.data?.error || "Failed to approve missing shipment")
    } finally {
      setUpdating(false)
    }
  }

  const handleReject = async () => {
    if (!validate({ rejectionReason }, validationRules)) return

    const confirmed = await Alert.confirm(
      "Are you sure you want to reject this missing shipment report?",
      "Reject Missing Report",
    )

    if (!confirmed) return

    try {
      setUpdating(true)

      await shipmentMissingService.rejectMissing(missingId, rejectionReason.trim())

      Alert.success("Missing shipment report rejected successfully!")
      navigate("/missing-shipments")
    } catch (error) {
      console.error("Error rejecting missing:", error)
      Alert.error(error.response?.data?.error || "Failed to reject missing shipment")
    } finally {
      setUpdating(false)
    }
  }

  const getStatusBadgeColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      approved: "bg-green-100 text-green-800 border-green-200",
      rejected: "bg-red-100 text-red-800 border-red-200",
    }
    return colors[status?.toLowerCase()] || "bg-gray-100 text-gray-800 border-gray-200"
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading missing shipment..." />
        </div>
      </DashboardLayout>
    )
  }

  if (!missingData) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[40vh] sm:min-h-[60vh]">
          <div className="text-center px-4">
            <p className="text-gray-600 font-semibold text-base sm:text-lg">Missing record not found</p>
            <Button variant="secondary" className="mt-4" onClick={() => navigate("/missing-shipments")}>
              Back to Missing Shipments
            </Button>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  const isPending = missingData.approval_status?.toLowerCase() === "pending"

  return (
    <DashboardLayout>
      <div>
        <div className="flex flex-col gap-3 lg:flex-row lg:justify-between lg:items-center mb-4 sm:mb-6">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-700 flex flex-wrap items-center gap-2 sm:gap-3">
              <AlertTriangle className="w-6 h-6 text-buttonhoverTxt shrink-0" />
              {isPending ? "Review" : t("common.view")} Missing Report
            </h1>
          </div>
          <div className="flex flex-col gap-2 w-full lg:w-auto md:flex-row md:flex-wrap md:gap-3">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold uppercase border ${getStatusBadgeColor(missingData.approval_status)}`}>
              {missingData.approval_status}
            </span>
            <Button variant="primary" onClick={() => navigate(`/shipments/view/${missingData.shipment_id}`)}>
              View Shipment
            </Button>
            <Button
              variant="secondary"
              icon={ArrowLeft}
              mobileIconOnly
              mobileLabel="Back to Missing Shipments"
              onClick={() => navigate("/missing-shipments")}
            >
              Back to Missing Shipments
            </Button>
          </div>
        </div>

        <SectionCard title="Missing Details">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-6">
            <InfoField label="Missing ID" value={`#${missingData.missing_id}`} />
            <InfoField label="Shipment ID">
              <button
                type="button"
                onClick={() => navigate(`/shipments/view/${missingData.shipment_id}`)}
                className="font-medium text-blue-600 hover:text-blue-700 hover:underline text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1 text-left w-full"
              >
                #{missingData.shipment_id}
              </button>
            </InfoField>
            <InfoField label={t('common.createdBy')} value={missingData.created_by || "N/A"} />
            <InfoField label="Quantity Missing" value={missingData.quantity_missing || "N/A"} />
            <InfoField
              label="Created Date"
              value={missingData.created_at ? formatIstDateTime(missingData.created_at) : "N/A"}
            />
            <InfoField label="Status">
              <span className={`inline-block px-3 py-1 mt-1 rounded-full text-xs font-semibold uppercase border ${getStatusBadgeColor(missingData.approval_status)}`}>
                {missingData.approval_status}
              </span>
            </InfoField>
          </div>
        </SectionCard>

        <SectionCard title="Reason for Missing">
          <p className="text-gray-900 whitespace-pre-wrap leading-relaxed bg-gray-100 p-3 rounded-xl">
            {missingData.reason}
          </p>
        </SectionCard>

        {missingData.missing_details && (
          <SectionCard title="Additional Details">
            <p className="text-gray-900 whitespace-pre-wrap leading-relaxed bg-gray-100 p-3 rounded-xl">
              {missingData.missing_details}
            </p>
          </SectionCard>
        )}

        {missingData.rejection_reason && (
          <SectionCard title="Rejection Reason">
            <p className="text-red-800 whitespace-pre-wrap leading-relaxed bg-red-50 p-3 rounded-xl border border-red-100">
              {missingData.rejection_reason}
            </p>
          </SectionCard>
        )}

        {isPending && (
          <SectionCard title="Approval Action">
            <div className="space-y-5">
              <div>
                <label className={formLabelClass}>
                  Rejection Reason
                  <span className="text-gray-500 text-xs ml-2 font-normal">(Required if rejecting)</span>
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => {
                    setRejectionReason(e.target.value)
                    validateField('rejectionReason', e.target.value, validationRules.rejectionReason)
                  }}
                  placeholder="Provide a detailed reason if you're rejecting this missing report..."
                  rows="4"
                  className={formTextareaClass(Boolean(errors.rejectionReason))}
                  disabled={updating}
                />
                <ValidationMessage error={errors.rejectionReason} />
                <div className="flex items-center justify-between mt-2 text-xs">
                  <span
                    className={`font-semibold ${rejectionReason.length < 10 ? "text-red-600" : "text-green-600"}`}
                  >
                    {rejectionReason.length} / 10 characters minimum
                  </span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <Button
                  variant="danger"
                  onClick={handleReject}
                  loading={updating}
                  disabled={updating || rejectionReason.trim().length < 10}
                >
                  {updating ? "Processing..." : "Reject Report"}
                </Button>
                <Button
                  variant="primary"
                  onClick={handleApprove}
                  loading={updating}
                  disabled={updating}
                >
                  {updating ? "Processing..." : "Approve Report"}
                </Button>
              </div>
            </div>
          </SectionCard>
        )}
      </div>
    </DashboardLayout>
  )
}

export default EditMissing;
